import { useState, MouseEvent, FC } from 'react';

import { useAtomValue } from 'jotai';
import { Clock, Trash2, Pencil, UserPlus, Download } from 'lucide-react';
import useSWRMutation from 'swr/mutation';
import { BaseMenu, BaseMenuItem } from 'wbs-components-lib';

import { TicketTableActionsMenuProps } from './types';

import { chanceTicketService } from 'v2/api/chanceTicketService';
import { userAtom } from 'v2/atoms/userAtom';
import { PRIZE_HEAD_SUPPORT_GROUP, TicketStatus, TicketType } from 'v2/constants';
import { handleHttpError, isRouteAllowed } from 'v2/utils';

export const TicketTableActionsMenu: FC<TicketTableActionsMenuProps> = ({
  isUserService,
  ticket,
  handleTicketEditModalOpen,
  handleExpireTicketModalOpen,
  handleDeleteTicketModalOpen,
  handleBulkDistributionModalOpen,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isMenuOpened = Boolean(menuAnchor);

  const user = useAtomValue(userAtom);
  const isExportAllowed = isRouteAllowed(user?.roles ?? [], PRIZE_HEAD_SUPPORT_GROUP);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const { id, status, type } = ticket;
  const isTicketExpired = status === TicketStatus.EXPIRED;

  const { trigger: triggerExportTickets, isMutating: isExportTicketsFetching } = useSWRMutation(
    ['exportChanceTicketTickets', id],
    chanceTicketService.exportTickets,
    { onError: handleHttpError }
  );

  const { trigger: triggerExportUsers, isMutating: isExportUsersFetching } = useSWRMutation(
    ['exportChanceTicketUsers', id],
    chanceTicketService.exportUsers,
    { onError: handleHttpError }
  );

  const baseMenuItems: BaseMenuItem[] = [
    {
      icon: <Pencil />,
      isDisabled: isUserService,
      text: 'Edit Ticket',
      onClick: () => handleTicketEditModalOpen(ticket),
    },
    {
      icon: <Clock />,
      text: 'Expire Ticket',
      onClick: () => handleExpireTicketModalOpen(ticket),
      isDisabled: status === TicketStatus.EXPIRED || isUserService,
    },
    {
      icon: <Trash2 />,
      isDisabled: isUserService,
      text: 'Delete Ticket',
      onClick: () => handleDeleteTicketModalOpen(ticket),
    },
  ];

  const contestTicketMenuItems: BaseMenuItem[] = [
    ...baseMenuItems,
    {
      icon: <UserPlus />,
      text: 'Bulk Distribution',
      onClick: () => handleBulkDistributionModalOpen(ticket),
      isDisabled: isTicketExpired,
    },
    ...(isExportAllowed
      ? ([
          {
            icon: <Download />,
            text: 'Export tickets',
            isDisabled: isExportTicketsFetching,
            onClick: () => {
              triggerExportTickets();
              handleCloseMenu();
            },
          },
          {
            icon: <Download />,
            text: 'Export users',
            isDisabled: isExportUsersFetching,
            onClick: () => {
              triggerExportUsers();
              handleCloseMenu();
            },
          },
        ] satisfies BaseMenuItem[])
      : []),
  ];

  const menuItems = type === TicketType.CONTEST ? contestTicketMenuItems : baseMenuItems;

  return (
    <BaseMenu
      key={id}
      menuItems={menuItems}
      menuAnchor={menuAnchor}
      isMenuOpened={isMenuOpened}
      handleOpenMenu={handleOpenMenu}
      handleCloseMenu={handleCloseMenu}
    />
  );
};
